import * as React from "react";
import { format } from "date-fns";
import { ConnectProto } from "@racoon/api";
import { useDateFnsLocale, useT } from "@racoon/i18n";
import { BrandIcImportant, IcPrint } from "@racoon/icons";
import { Check, FileDown } from "lucide-react";
import { fromTimestamp } from "@racoon/lib";
import {
  Button,
  type ButtonProps,
  cn,
  PlateNumber,
  StatusPill,
  Tabs,
  TabsList,
  TabsTrigger,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@racoon/ui";
import {
  humanTruckType,
  shiftStatusLabel,
  shiftStatusTone,
} from "~/pages/dashboard/autopark/helpers";
import { fullName } from "~/shared/helpers";

export type ShiftTab = {
  id: bigint;
  label: string;
  status: ConnectProto.DriverOnShiftStatus;
};

type FolderTab<T> = { value: T; label: React.ReactNode; leading?: React.ReactNode };

/**
 * Figma "Tab style 2": `@racoon/ui`'s folder tabs, 24px in from the row's bottom rule, with
 * `trailing` at the row's end. Tabs stretch to the tallest (a counter badge makes one taller).
 */
export const FolderTabs = <T extends string | bigint>({
  tabs,
  value,
  onChange,
  className,
  center,
  trailing,
}: {
  tabs: FolderTab<T>[];
  value: T | null;
  onChange: (value: T) => void;
  className?: string;
  /** Sits centred between the tabs and `trailing`. */
  center?: React.ReactNode;
  trailing?: React.ReactNode;
}) => (
  <Tabs
    value={value === null ? "" : String(value)}
    onValueChange={(next) => {
      const tab = tabs.find((t) => String(t.value) === next);
      if (tab) onChange(tab.value);
    }}
    className={cn("flex shrink-0 items-end border-b border-ds-border-primary pl-6", className)}
  >
    <TabsList variant="folder" className="!w-auto !items-stretch !border-0">
      {tabs.map((tab) => (
        <TabsTrigger
          key={String(tab.value)}
          value={String(tab.value)}
          variant="folder"
          className="gap-1 !py-2 !pl-2 !pr-3 !font-bold leading-4 !tracking-normal !text-ds-text-secondary data-[state=active]:!text-ds-text-body"
        >
          {tab.leading}
          {tab.label}
        </TabsTrigger>
      ))}
    </TabsList>
    {/* In the flow, centred in what the tabs and `trailing` leave: never on top of either. */}
    {center && (
      <div className="flex min-w-0 flex-1 items-center justify-center self-stretch px-4">
        {center}
      </div>
    )}
    {trailing && <div className="ml-auto flex min-w-0 items-center self-stretch">{trailing}</div>}
  </Tabs>
);

/** The route sheet's and TTN's 64px header: shift tabs, the page's actions on the right. */
export const ShiftHeader: React.FC<{
  tabs: ShiftTab[];
  activeId: bigint | null;
  onChange: (id: bigint) => void;
  /** E.g. the unassigned jobs chip, centred in the row. */
  center?: React.ReactNode;
  actions?: React.ReactNode;
}> = ({ tabs, activeId, onChange, center, actions }) => (
  <FolderTabs
    className="h-16"
    center={center}
    value={activeId}
    onChange={onChange}
    tabs={tabs.map((tab) => ({
      value: tab.id,
      label: tab.label,
      leading: (
        <span className="flex h-4 w-4 items-center justify-center">
          <span className={cn("h-2 w-2 rounded-full", shiftStatusTone(tab.status).dot)} />
        </span>
      ),
    }))}
    trailing={actions && <div className="flex items-center gap-4 p-3">{actions}</div>}
  />
);

/** "Routes / TTN not approved" — Figma attention-subtle pill. */
export const ApprovalPill: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div className="flex h-8 items-center gap-1 rounded-[8px] bg-ds-surface-attention-subtle px-1.5 py-1">
    <BrandIcImportant className="h-6 w-6" />
    <span className="whitespace-nowrap px-1 text-xs font-bold uppercase leading-4 text-ds-text-body">
      {children}
    </span>
  </div>
);

/**
 * Says why a control is disabled, in a keyboard-reachable tooltip. A disabled button takes
 * neither pointer events nor focus, so the wrapper is the trigger.
 */
const WithReason: React.FC<{ reason?: string; children: React.ReactNode }> = ({
  reason,
  children,
}) =>
  reason ? (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <span
            tabIndex={0}
            className="inline-flex gap-2 rounded-lg outline-none focus-visible:ring-2 focus-visible:ring-ds-border-action"
          >
            {children}
          </span>
        </TooltipTrigger>
        <TooltipContent side="bottom">{reason}</TooltipContent>
      </Tooltip>
    </TooltipProvider>
  ) : (
    <span className="inline-flex gap-2">{children}</span>
  );

/** Figma Primary button with its "Checked Checkbox" icon: approve routes / TTN. */
export const ApproveButton: React.FC<ButtonProps & { reason?: string }> = ({
  children,
  reason,
  disabled,
  ...props
}) => (
  <WithReason reason={reason}>
    <Button variant="dsPrimary" size="md" disabled={disabled || Boolean(reason)} {...props}>
      <span className="flex h-4 w-4 items-center justify-center rounded-[3px] bg-white">
        <Check className="h-3 w-3 text-ds-icon-action" strokeWidth={3} />
      </span>
      {children}
    </Button>
  </WithReason>
);

/** The document's print (Secondary) and download (Accent Green) icon buttons; `reason` disables both and says why. */
export const DocumentButtons: React.FC<{
  onPrint: () => void;
  onDownload: () => void;
  printLabel: string;
  downloadLabel: string;
  disabled?: boolean;
  reason?: string;
  idPrefix?: string;
}> = ({ onPrint, onDownload, printLabel, downloadLabel, disabled, reason, idPrefix }) => (
  <WithReason reason={reason}>
    <Button
      id={idPrefix && `${idPrefix}-print-button`}
      variant="dsSecondary"
      size="md"
      className="w-10"
      title={printLabel}
      aria-label={printLabel}
      disabled={disabled || Boolean(reason)}
      onClick={onPrint}
    >
      <IcPrint className="h-4 w-4 shrink-0" />
    </Button>
    <Button
      id={idPrefix && `${idPrefix}-download-button`}
      variant="dsSuccess"
      size="md"
      className="w-10"
      title={downloadLabel}
      aria-label={downloadLabel}
      disabled={disabled || Boolean(reason)}
      onClick={onDownload}
    >
      <FileDown className="h-4 w-4 shrink-0" />
    </Button>
  </WithReason>
);

/** The first of `fact`, `plan` that is set, as HH:mm. */
const formatTime = (
  fact: ConnectProto.DriverOnShift["startedAt"],
  plan: ConnectProto.DriverOnShift["twStartAt"],
) => {
  const date = fromTimestamp(fact) ?? fromTimestamp(plan);
  return date ? format(date, "HH:mm") : "--:--";
};

/**
 * The shift's two summary rows: status pill + crew, with date and time window on the right;
 * plate + vehicle details. The TTN puts the vehicle row first.
 */
export const ShiftSummary: React.FC<{
  shift: ConnectProto.DriverOnShift | undefined;
  vehicle: ConnectProto.Truck | undefined;
  orgName: string | undefined;
  date: Date | undefined;
  ttnApproved?: boolean;
  vehicleFirst?: boolean;
  /** Stands in for the driver's name, e.g. the assign-driver button. */
  driverSlot?: React.ReactNode;
  driverFallback: string;
}> = ({ shift, vehicle, orgName, date, ttnApproved, vehicleFirst, driverSlot, driverFallback }) => {
  const t = useT();
  const locale = useDateFnsLocale();

  const crewRow = (
    <div className="flex min-h-6 items-center justify-between gap-4">
      <div className="flex min-w-0 items-center gap-4 whitespace-nowrap text-sm">
        {shift && (
          <StatusPill tone={shiftStatusTone(shift.status).badge}>
            {shiftStatusLabel(shift.status, t, ttnApproved)}
          </StatusPill>
        )}
        {driverSlot ?? (
          <span className="font-medium leading-5 text-ds-text-body">
            {fullName(shift?.driver) || driverFallback}
          </span>
        )}
        {shift?.crew.map((user) => (
          <span key={String(user.id)} className="truncate leading-4 text-ds-text-secondary">
            {fullName(user)}
          </span>
        ))}
      </div>
      {shift && (
        <div className="flex items-center gap-4 whitespace-nowrap text-sm font-medium leading-5 text-ds-text-secondary">
          {date && <span>{format(date, "d MMM yyyy", { locale })}</span>}
          <span>
            {formatTime(shift.startedAt, shift.twStartAt)} -{" "}
            {formatTime(shift.completedAt, shift.twEndAt)}
          </span>
        </div>
      )}
    </div>
  );

  const vehicleRow = (
    <div className="flex h-8 items-center gap-4 whitespace-nowrap text-sm leading-4 text-ds-text-secondary">
      <PlateNumber size="lg" plateNumber={vehicle?.plateNumber ?? "--"} />
      {vehicle?.truckType?.manufacturer && <span>{vehicle.truckType.manufacturer}</span>}
      <span>{vehicle?.truckType?.model ?? vehicle?.model ?? "--"}</span>
      <span>{humanTruckType(vehicle?.truckType?.type ?? vehicle?.type ?? "--", t)}</span>
      <span>{orgName ?? "--"}</span>
    </div>
  );

  return (
    <div className="flex shrink-0 flex-col gap-4">
      {vehicleFirst ? vehicleRow : crewRow}
      {vehicleFirst ? crewRow : vehicleRow}
    </div>
  );
};
